<template>
  <v-form ref="form" v-model="valid" lazy-validation @close="close" @submit="submit" @submit.prevent="save">
    <v-card class="edit-client-modal-card">
      <v-card-title class="edit-client-modal-title">
        <span class="text-h6">{{ message }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          text
          class="btn__icon pa-0"
          width="32"
          height="32"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="edit-client-modal-text">
        <v-container class="pa-0">
          <v-row>
            <v-col v-if="showWithoutDoorphoneOption" cols="12" class="py-2">
              <v-checkbox
                v-model="withoutDoorphoneChecked"
                label="Без домофона (только шлагбаумы)"
                hide-details
                color="primary"
                class="mt-0"
                :disabled="initialWithoutDoorphone"
                @change="onWithoutDoorphoneChange"
              />
            </v-col>
            <v-col cols="12" class="pb-2 pt-0">
              <v-text-field
                v-model="attPopupEditContent.name"
                outlined
                dense
                label="ФИО"
                type="string"
                prepend-inner-icon="mdi-account-outline"
                :rules="[v => !v || true]"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" class="py-2">
              <v-text-field
                v-model.trim="attPopupEditContent.email"
                outlined
                dense
                label="Email"
                type="email"
                prepend-inner-icon="mdi-email-outline"
                :rules="[v => !v || /.+@.+\..+/.test(v) || 'Введите корректный e-mail']"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" class="py-2">
              <v-text-field
                v-model.trim="attPopupEditContent.telephone"
                outlined
                dense
                disabled
                label="Номер телефона"
                prepend-inner-icon="mdi-phone-outline"
                :rules="[v => !!v || 'Обязательное поле']"
                required
                hide-details="auto"
              />
            </v-col>

            <template v-if="showWithoutDoorphoneOption && withoutDoorphoneChecked">
              <v-col cols="12" class="py-2">
                <v-autocomplete
                  v-model="attPopupEditContent.barrier_ids"
                  :items="barrierOptions"
                  :item-text="barrierDisplayText"
                  item-value="id"
                  label="Шлагбаумы (DS07P-LP)"
                  placeholder="Поиск по адресу или названию"
                  prepend-inner-icon="mdi-gate"
                  outlined
                  dense
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hide-selected
                  clearable
                  :loading="barriersLoading"
                  :rules="editBarrierRules"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" class="py-2">
                <VehicleNumberInput
                  :value="singleCarNumberDisplay"
                  label="Номер ТС"
                  :required="true"
                  disabled
                  @input="onSingleCarNumberInput"
                />
              </v-col>
            </template>

            <v-col v-else cols="12" class="pt-2 pb-0">
              <VehicleNumberInput
                :value="carNumbersDisplay"
                label="Номер автомобиля"
                disabled
                @input="onCarNumberInput"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="edit-client-modal-actions">
        <v-spacer />
        <v-btn
          color="primary"
          rounded
          :disabled="!valid || (withoutDoorphoneChecked && ( !barrierIdsLength || !(singleCarNumberDisplay || '').trim() ))"
          @click="save"
        >
          Сохранить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { mapActions } from 'vuex';
import VehicleNumberInput from '@/components/VehicleNumberInput.vue';

const CAR_NUMBER_REG = /^[0-9АВЕКМНОРСТУХA-Za-z]+$/u;

export default {
  name: 'EditClientModal',
  components: { VehicleNumberInput },
  props: {
    active: { type: Boolean, required: true },
    id: { type: Number },
    message: { type: String },
    initialWithoutDoorphone: {
      type: Boolean,
      default: false,
    },
    showWithoutDoorphoneOption: {
      type: Boolean,
      default: false,
    },
    attPopupEditContent: {
      type: Object,
      default: () => ({
        name: '',
        email: '',
        telephone: '',
        car_numbers: [],
        without_doorphone: false,
        barrier_ids: [],
      }),
    },
  },
  data () {
    return {
      valid: true,
      barrierOptions: [],
      barriersLoading: false,
      withoutDoorphoneChecked: false,
      carNumberRules: [
        v => !v || CAR_NUMBER_REG.test(v) || 'Только символы: 0-9, А, В, Е, К, М, Н, О, Р, С, Т, У, Х (латиница или кириллица).',
        v => !v || (v && v.length <= 20) || 'Не более 20 символов.',
      ],
    };
  },
  computed: {
    carNumbersDisplay () {
      const arr = this.attPopupEditContent.car_numbers;
      if (Array.isArray(arr) && arr.length > 0) {
        const first = arr[0];
        if (first && typeof first === 'object' && first.car_number != null) return first.car_number;
        if (typeof first === 'string') return first;
      }
      if (this.attPopupEditContent.car_number && typeof this.attPopupEditContent.car_number === 'string') {
        return this.attPopupEditContent.car_number;
      }
      return '';
    },
    carNumbersArray () {
      const arr = this.attPopupEditContent.car_numbers;
      if (!Array.isArray(arr)) return [];
      return arr.map(item =>
        item && typeof item === 'object' && item.car_number != null ? item.car_number : String(item || ''),
      ).filter(Boolean);
    },
    singleCarNumberDisplay () {
      const arr = this.carNumbersArray;
      return arr.length > 0 ? arr[0] : '';
    },
    editCarNumberRules () {
      if (!this.withoutDoorphoneChecked) return [];
      return [
        v => !!((v || '').trim()) || 'Укажите гос. номер',
        v => !v || CAR_NUMBER_REG.test(String(v).trim()) || 'Только цифры и буквы (А, В, Е, К, М, Н, О, Р, С, Т, У, Х).',
        v => !v || (v && v.length <= 20) || 'Не более 20 символов.',
      ];
    },
    barrierIdsLength () {
      const ids = this.attPopupEditContent.barrier_ids;
      return Array.isArray(ids) ? ids.length : 0;
    },
    barrierDisplayText () {
      return (barrier) => {
        if (!barrier) return '';
        const name = barrier.name || '';
        const addr = barrier.address || '';
        return name && addr ? `${name} — ${addr}` : (name || addr || `ID ${barrier.id}`);
      };
    },
    editBarrierRules () {
      if (!this.withoutDoorphoneChecked) return [];
      return [
        v => (Array.isArray(v) && v.length > 0) || 'Выберите хотя бы один шлагбаум',
      ];
    },
  },
  watch: {
    active: {
      handler (visible) {
        if (visible) {
          this.withoutDoorphoneChecked = !!this.initialWithoutDoorphone;
          this.$set(this.attPopupEditContent, 'without_doorphone', this.withoutDoorphoneChecked);
          this.ensureBarrierIdsArray();
          this.$nextTick(() => {
            if (this.showWithoutDoorphoneOption && this.initialWithoutDoorphone && this.barrierOptions.length === 0) {
              this.loadBarriers();
            }
          });
        }
      },
      immediate: true,
    },
    initialWithoutDoorphone (val) {
      if (this.active) {
        this.withoutDoorphoneChecked = !!val;
        this.$set(this.attPopupEditContent, 'without_doorphone', this.withoutDoorphoneChecked);
      }
    },
  },
  methods: {
    ...mapActions({
      updateClient: 'Client/updateClient',
      loadAllBarriers: 'Barriers/loadAllBarriers',
    }),
    ensureBarrierIdsArray () {
      if (!Array.isArray(this.attPopupEditContent.barrier_ids)) {
        this.$set(this.attPopupEditContent, 'barrier_ids', []);
      }
    },
    onWithoutDoorphoneChange () {
      this.$set(this.attPopupEditContent, 'without_doorphone', this.withoutDoorphoneChecked);
      if (this.withoutDoorphoneChecked) {
        this.ensureBarrierIdsArray();
        if (this.barrierOptions.length === 0) this.loadBarriers();
        if (this.carNumbersArray.length === 0) {
          const fromClient = this.carNumbersDisplay;
          if (fromClient) {
            const arr = this.attPopupEditContent.car_numbers;
            const normalized = Array.isArray(arr) ? arr.map(item =>
              item && typeof item === 'object' && item.car_number != null ? item.car_number : String(item || ''),
            ).filter(Boolean) : [];
            if (normalized.length === 0) {
              this.$set(this.attPopupEditContent, 'car_numbers', [fromClient.trim()]);
            }
          }
        }
      }
    },
    loadBarriers () {
      this.barriersLoading = true;
      this.loadAllBarriers({ limit: 1000 })
        .then((res) => {
          const list = res.data || [];
          this.barrierOptions = list.filter(b =>
            (b.model || (b.device && b.device.model) || '').toString() === 'DS07P-LP',
          );
        })
        .catch(() => {
          this.$flash_warning('Не удалось загрузить список шлагбаумов');
          this.barrierOptions = [];
        })
        .finally(() => {
          this.barriersLoading = false;
        });
    },
    onSingleCarNumberInput (value) {
      const filtered = (value || '').toString().replace(/[^0-9АВЕКМНОРСТУХA-Za-z]/gu, '').toUpperCase().slice(0, 20);
      this.$set(this.attPopupEditContent, 'car_numbers', filtered ? [filtered] : []);
    },
    onCarNumberInput (value) {
      const filtered = (value || '').toString().replace(/[^0-9АВЕКМНОРСТУХA-Za-z]/gu, '').toUpperCase();
      this.$set(this.attPopupEditContent, 'car_numbers', filtered ? [filtered] : []);
    },
    save () {
      this.$refs.form.validate();
      this.$nextTick(() => {
        if (!this.valid) return;
        if (this.attPopupEditContent.without_doorphone && (this.barrierIdsLength === 0 || !(this.singleCarNumberDisplay || '').trim())) {
          this.$flash_warning('Укажите шлагбаумы и хотя бы один гос. номер');
          return;
        }
        let carNumbers = this.carNumbersArray;
        if (!this.attPopupEditContent.without_doorphone) {
          const arr = this.attPopupEditContent.car_numbers;
          carNumbers = Array.isArray(arr)
            ? arr.map(item => (item && typeof item === 'object' && item.car_number != null ? item.car_number : item)).filter(Boolean)
            : [];
          if (carNumbers.length === 0 && this.attPopupEditContent.car_number) {
            const cn = this.attPopupEditContent.car_number;
            carNumbers = [typeof cn === 'string' ? cn : (cn && cn.car_number) || ''].filter(Boolean);
          }
        }
        const payload = {
          id: this.id,
          name: this.attPopupEditContent.name,
          email: this.attPopupEditContent.email,
          telephone: this.attPopupEditContent.telephone,
          car_numbers: carNumbers.length ? carNumbers : [],
          without_doorphone: !!this.attPopupEditContent.without_doorphone,
        };
        if (this.attPopupEditContent.without_doorphone) {
          payload.barrier_ids = Array.isArray(this.attPopupEditContent.barrier_ids) ? this.attPopupEditContent.barrier_ids : [];
        }
        this.close();
        this.updateClient(payload)
          .then(() => {
            this.$flash_success('Изменения успешно сохранены!');
            this.submit();
          })
          .catch((err) => {
            const data = err.response && err.response.data;
            if (data && typeof data === 'object') {
              Object.entries(data).forEach(([field, messages]) => {
                const list = Array.isArray(messages) ? messages : [messages];
                list.forEach(m => this.$flash_warning(`${field}: ${m}`));
              });
            } else {
              this.$flash_warning('Ошибка при редактировании клиента');
            }
          });
      });
    },
    async submit () {
      this.$emit('submit');
    },
    async close () {
      this.$emit('close');
      this.$emit('update:active', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-client-modal-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}
.edit-client-modal-title {
  padding: 20px 24px 16px;
  align-items: center;
}
.edit-client-modal-title .text-h6 {
  font-weight: 600;
}
.edit-client-modal-text {
  padding: 20px 24px;
}
.edit-client-modal-actions {
  padding: 12px 24px 20px;
}
.edit-client-modal-actions .v-btn {
  text-transform: none;
  font-weight: 500;
}
</style>
